<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import { useTheme } from 'vuetify';
import axios from 'axios';

const router = useRouter();
const theme = useTheme();

// The step is a runtime <component :is>, so it has no static type — declare the one
// member the wizard calls on it rather than falling back to `any`.
const stepRef = useTemplateRef<any>('stepRef');

const step = ref(1);
const stepValid = ref(false);
const error = ref<any>(null);
const processing = ref(false);
const stepData = ref<any>(null);

function stepLoaded(newStep: any) {
	step.value = newStep;
}

function validStateChanged(valid: any) {
	stepValid.value = valid;
}

function previous() {
	step.value = step.value - 1;
	router.push('/step' + step.value);
}

function next() {
	if (stepRef.value?.process) {
		stepRef.value.process(() => {
			step.value = step.value + 1;
			router.push('/step' + step.value);
		});
	} else {
		step.value = step.value + 1;
		router.push('/step' + step.value);
	}
}

onMounted(() => {
	(theme as any).change('dark');

	processing.value = true;
	axios
		.get('/api/setup/status')
		.then((response: any) => {
			stepData.value = response.data;
			step.value = response.data.resultingStep;
			router.push('/step' + step.value);
			processing.value = false;
		})
		.catch((err: any) => {
			error.value = err.response.data.error[0];
		});
});
</script>
<template>
	<v-main>
		<v-container fluid class="fill-height">
			<v-row align="center" justify="center">
				<v-col cols="12" md="6">
					<v-card class="elevation-12">
						<v-toolbar theme="dark" color="primary">
							<v-toolbar-title>VEVA - Setup</v-toolbar-title>
						</v-toolbar>
						<v-card-text>
							<v-alert v-if="error" type="error">
								{{ error.message }}
							</v-alert>

							<div v-if="!error && !processing">
								<v-progress-linear v-if="processing" indeterminate />

								<v-stepper v-model="step">
									<v-stepper-header>
										<v-stepper-item :complete="step > 1" :value="1" title="Database connection" />
										<v-divider />
										<v-stepper-item :complete="step > 2" :value="2" title="Database setup" />
										<v-divider />
										<v-stepper-item :complete="step > 3" :value="3" title="User setup" />
										<v-divider />
										<v-stepper-item :complete="step > 4" :value="4" title="Website setup" />
										<v-divider />
										<v-stepper-item :complete="step > 5" :value="5" title="Done" />
										<v-divider />
									</v-stepper-header>
								</v-stepper>
								<v-card>
									<v-card-text>
										<router-view #default="{ Component }">
											<component
												ref="stepRef"
												:is="Component"
												:status="stepData"
												@step-loaded="stepLoaded"
												@step-valid="validStateChanged"
											/>
										</router-view>
									</v-card-text>
								</v-card>
							</div>
						</v-card-text>
						<v-card-actions v-if="!error">
							<v-btn :disabled="step == 1" variant="text" @click="previous()">&laquo; Previous</v-btn>
							<v-spacer />
							<v-btn :disabled="step >= 5 || !stepValid" variant="text" @click="next()">Next &raquo;</v-btn>
						</v-card-actions>
					</v-card>
				</v-col>
			</v-row>
		</v-container>
	</v-main>
</template>
